// src/shared/changelog/releaseEntry.ts
import { checkEntryCopy } from "./entryCopy";
import type { ChangeCategory } from "./types";

/**
 * The changelog entry a Reflector V2 release carries while it is being authored
 * (CTX-456), mirroring the four nullable `ReflectorReleases` columns CTX-454
 * added. `setLatest` is what turns it into a real entry (CTX-455).
 *
 * Kept out of the modal so the rule below can be tested without rendering a
 * form — the same reason `publicFeed.ts` sits beside its script rather than
 * inside it.
 *
 * The two free-text fields are plain strings rather than `string | null`
 * because they are bound to inputs, whose empty state is `""`. `category` and
 * `isPublic` have no such natural empty value, so they carry `null`.
 */
export interface ReleaseEntryDraft {
  title: string;
  description: string;
  category: ChangeCategory | null;
  /** Three states on purpose — see `changelogIsPublic` on the model. */
  isPublic: boolean | null;
  /**
   * Optional *within* an entry, matching `action_required` in the entry files.
   * Empty is a claim — the customer has nothing to do — so the form prompts for
   * it rather than hiding it, but it does not force a sentence: for most
   * changes "nothing to do" is simply true.
   */
  actionRequired: string;
}

export const EMPTY_RELEASE_ENTRY: ReleaseEntryDraft = {
  title: "",
  description: "",
  category: null,
  isPublic: null,
  actionRequired: "",
};

/**
 * No entry at all, which is a legitimate release: an internal patch or a
 * rebuild promotes and announces nothing.
 */
export function isReleaseEntryEmpty(draft: ReleaseEntryDraft): boolean {
  return (
    draft.title.trim() === "" &&
    draft.description.trim() === "" &&
    draft.category === null &&
    draft.isPublic === null &&
    // Counted here even though it is not required below: someone who wrote only
    // this has started an entry, and should be told what else it needs rather
    // than have the sentence silently dropped.
    draft.actionRequired.trim() === ""
  );
}

/**
 * Every problem with a draft entry, or an empty array.
 *
 * ── Why all-or-nothing ──────────────────────────────────────────────────────
 *
 * The fields are optional as a group, never individually. A half-filled entry
 * is the one outcome worse than no entry: the author believes they announced
 * the release, and at promotion time it either writes nothing or writes
 * something the entry contract refuses — `isPublic` has to be an explicit
 * boolean, so a forgotten toggle cannot become a row.
 *
 * Either way the author is not there to see it. Failing here, while they are
 * still typing, is the whole point of validating in the form: the rejection
 * belongs at authoring time, not at promotion time.
 *
 * ── Why the copy rules come from `entryCopy` ────────────────────────────────
 *
 * Same predicate *and* same message as the CI linter. Copy that passes this
 * form and then fails CI would teach authors that the two are different rules.
 */
export function checkReleaseEntry(draft: ReleaseEntryDraft): string[] {
  if (isReleaseEntryEmpty(draft)) return [];

  const problems: string[] = [];
  const title = draft.title.trim();
  const description = draft.description.trim();

  if (title === "") {
    problems.push("title is required once the entry has any other field.");
  }
  if (description === "") {
    problems.push(
      "description is required once the entry has any other field."
    );
  }
  if (draft.category === null) {
    problems.push("category is required once the entry has any other field.");
  }
  if (draft.isPublic === null) {
    problems.push(
      "public has no default — choose whether this entry goes out on the " +
        "open internet."
    );
  }

  // Only on text that exists: `checkEntryCopy("")` is silent, but pairing
  // "title is required" with a length verdict on an empty string reads like
  // the form is arguing with itself.
  //
  // `actionRequired` is not checked. `EntryCopyField` covers the two fields the
  // CI linter bounds, and it is mirrored by hand in chopper-backend (CTX-348) —
  // widening it here would put the two repos' copies out of step for a rule CI
  // does not apply.
  if (title !== "") problems.push(...checkEntryCopy("title", title));
  if (description !== "") {
    problems.push(...checkEntryCopy("description", description));
  }

  return problems;
}
